import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { FormInput } from '../../../../components/forms/FormInput';
import { useAuth } from '../../../../contexts/AuthContext';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart, PatientDoc, IsSigningObject, FileTypes } from '@usacsmideo/types';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { WitnessSignatureArea } from '../../../../components/WitnessSignatureArea';
import { SignatureArea } from '../../../../components/SignatureArea';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { Button } from '../../../../components/buttons/Button';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import { applyEmployeeWitnesses, removeEmployeeWitnesses } from '../../../../data/employeeWitnesses';

export const Route = createFileRoute('/_authenticated/$patientId/statement/finalSignatures')({
  component: FinalSignaturesPage,
});

function FinalSignaturesPage() {
  const navigate = useNavigate();
  const { mutate: mutateChartDoc } = useUpdateChartSection();
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [updated, setUpdated] = React.useState(false);
  const [witnessOneName, setWitnessOneName] = React.useState('');
  const [witnessOneEmail, setWitnessOneEmail] = React.useState('');
  const [witnessOneIsDocuSign, setWitnessOneIsDocuSign] = React.useState(false);
  const [witnessTwoName, setWitnessTwoName] = React.useState('');
  const [witnessTwoEmail, setWitnessTwoEmail] = React.useState('');
  const [witnessTwoIsDocuSign, setWitnessTwoIsDocuSign] = React.useState(false);
  const [okToSubmit, setOkToSubmit] = React.useState(false);
  const [preferMideoWitness, setPreferMideoWitness] = React.useState(false);
  const [isApplyingWitnesses, setIsApplyingWitnesses] = React.useState(false);
  const [isSigning, setIsSigning] = React.useState({
    patientFinal: false,
    witness1: false,
    witness2: false,
    hipaa: false,
    consent: false,
    forms: false,
    abnMedicare: false,
    abn: false,
    aob1: false,
    aob2: false,
  } as IsSigningObject);
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  // SET WITNESS NAMES LOCALLY
  React.useEffect(() => {
    if (chart?.WITNESS_SIGNATURE?.witnessName?.length > 0) {
      setWitnessOneName(chart.WITNESS_SIGNATURE.witnessName);
    }
    if (chart?.WITNESS_SIGNATURE?.witness2Name?.length > 0) {
      setWitnessTwoName(chart.WITNESS_SIGNATURE.witness2Name);
    }
  }, [chart]);

  // SET WITNESS EMAILS LOCALLY
  React.useEffect(() => {
    if (chart?.WITNESS_SIGNATURE?.witnessEmail?.length > 0) {
      setWitnessOneEmail(chart.WITNESS_SIGNATURE.witnessEmail);
    }
    if (chart?.WITNESS_SIGNATURE?.witness2Email?.length > 0) {
      setWitnessTwoEmail(chart.WITNESS_SIGNATURE.witness2Email);
    }
  }, [chart]);

  // INITIALIZE MIDEO EMPLOYEE WITNESS PREFERENCE FROM CHART
  React.useEffect(() => {
    if (chart?.WITNESS_SIGNATURE?.preferMideoEmployeeWitness) {
      setPreferMideoWitness(true);
    }
  }, [chart?.WITNESS_SIGNATURE?.preferMideoEmployeeWitness]);

  // HANDLE MIDEO EMPLOYEE WITNESS CHECKBOX CHANGE
  const handleMideoWitnessChange = async (checked: boolean) => {
    setIsApplyingWitnesses(true);
    try {
      if (checked) {
        const result = await applyEmployeeWitnesses(patientId);
        if (result.success) {
          setPreferMideoWitness(true);
          chartQuery.refetch();
        } else {
          alert('Failed to apply MIDEO employee witnesses. Please try again.');
        }
      } else {
        const result = await removeEmployeeWitnesses(patientId);
        if (result.success) {
          setPreferMideoWitness(false);
          chartQuery.refetch();
        } else {
          alert('Failed to remove employee witnesses. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error handling employee witness change:', error);
      const message = error instanceof Error ? error.message : 'An error occurred. Please try again.';
      alert(message);
    } finally {
      setIsApplyingWitnesses(false);
    }
  };

  // WATCH FOR ABILITY TO SUBMIT
  React.useEffect(() => {
    // PATIENT HAS NOT YET SIGNED
    if (
      !chart?.PATIENT_SIGNATURE?.signature1DataURI ||
      (chart?.PATIENT_SIGNATURE?.signature1DataURI && chart.PATIENT_SIGNATURE.signature1DataURI?.length === 0)
    ) {
      setOkToSubmit(false);
      return;
    }

    // IF USING MIDEO EMPLOYEE WITNESSES
    if (preferMideoWitness) {
      if (
        chart?.WITNESS_SIGNATURE?.preferMideoEmployeeWitness &&
        chart?.WITNESS_SIGNATURE?.signature1DataURI?.length > 0 &&
        chart?.WITNESS_SIGNATURE?.signature2DataURI?.length > 0
      ) {
        setOkToSubmit(true);
      } else {
        setOkToSubmit(false);
      }
      return;
    }

    // EXISTING VALIDATION FOR DOCUSIGN/LOCAL WITNESSES
    // WITNESS IS UNNAMED
    if (witnessOneName?.length === 0 || witnessTwoName?.length === 0) {
      setOkToSubmit(false);
    }

    // LOCAL WITNESS ONE HAS NOT SIGNED
    else if (!witnessOneIsDocuSign && witnessOneName.length === 0) {
      setOkToSubmit(false);
    }

    // LOCAL WITNESS TWO HAS NOT SIGNED
    else if (!witnessTwoIsDocuSign && witnessTwoName.length === 0) {
      setOkToSubmit(false);
    }

    // DOCUSIGN WITNESS ONE HAS NOT SIGNED
    else if (
      (witnessOneIsDocuSign && witnessOneEmail.length === 0) ||
      (witnessOneIsDocuSign && chart.WITNESS_SIGNATURE?.signature1Docusign === undefined)
    ) {
      setOkToSubmit(false);
    }

    // DOCUSIGN WITNESS TWO HAS NOT SIGNED
    else if (
      (witnessTwoIsDocuSign && witnessTwoEmail.length === 0) ||
      (witnessTwoIsDocuSign && chart.WITNESS_SIGNATURE?.signature2Docusign === undefined)
    ) {
      setOkToSubmit(false);
    }

    // PATIENT AND BOTH WITNESSES HAVE SIGNED
    else {
      setOkToSubmit(true);
    }
  }, [
    preferMideoWitness,
    witnessOneName,
    witnessTwoName,
    witnessOneIsDocuSign,
    witnessTwoIsDocuSign,
    chart,
    witnessOneEmail?.length,
    witnessTwoEmail?.length,
  ]);

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'finalSignatures', patientInfo, value: true });
    }
  }, [patientInfo]);

  // WRITE FORM DATA TO FIRESTORE
  const handleIdInfoUpdate = () => {
    mutateChartDoc(
      {
        patientId: patientId,
        docData: {
          WITNESS_SIGNATURE: {
            witnessName: witnessForm.state.values.witnessName,
            witnessEmail: chart?.WITNESS_SIGNATURE?.witnessEmail ? chart.WITNESS_SIGNATURE.witnessEmail : '',
            signature1DataURI: chart?.WITNESS_SIGNATURE?.signature1DataURI
              ? chart.WITNESS_SIGNATURE.signature1DataURI
              : '',
            witness2Name: witnessForm.state.values.witness2Name,
            witness2Email: chart?.WITNESS_SIGNATURE?.witness2Email ? chart.WITNESS_SIGNATURE.witness2Email : '',
            signature2DataURI: chart?.WITNESS_SIGNATURE?.signature2DataURI
              ? chart.WITNESS_SIGNATURE.signature2DataURI
              : '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          setUpdated(true);
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // INITIALIZE FORM
  const witnessForm = useForm({
    defaultValues: {
      witnessName: chart?.WITNESS_SIGNATURE?.witnessName ? chart.WITNESS_SIGNATURE.witnessName : '',
      witness2Name: chart?.WITNESS_SIGNATURE?.witness2Name ? chart.WITNESS_SIGNATURE.witness2Name : '',
    },
    onSubmit: async () => {
      await handleIdInfoUpdate();
    },
  });

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        <div id="final-signatures-page" className="flex w-screen flex-col items-center justify-center">
          <Heading id="final-signatures-page-header" textContent="Final Signatures Page" />
          {/* <button
            onClick={() => {
              // throw new Error('Testing release based error boundaries');
              testCloudFunction();
            }}
          >
            Break the world
          </button> */}
          <div id="final-signatures-page-content" className="flex w-5/6 flex-row justify-around">
            <div id="script-container" className="w-full p-4">
              <h2 id="patient-final-sig-header" className="text-2xl font-bold">
                Patient
              </h2>
              <div id="patient-final-sig-container" className="">
                {chart.PATIENT_SIGNATURE && (
                  <SignatureArea
                    elementId={'patient-final-sig-'}
                    type={FileTypes.patientSig}
                    data={chart}
                    patientId={patientId}
                    isSigning={isSigning}
                    setIsSigning={setIsSigning}
                    cloudStoragePath={`/user/${patientId}/signatures/PATIENT_SIGNATURE_PatientFinalSignature`}
                    docToUpdate={'PATIENT_SIGNATURE'}
                    sigName={'patientFinal'}
                    canComeFromClone={true}
                  />
                )}
              </div>
              {/* MIDEO EMPLOYEE WITNESS OPTION - CONCIERGE ONLY */}
              {patientInfo?.navType?.base === 'concierge' && (
                <div className="my-8 rounded-lg bg-gray-100 p-4">
                  <label className="flex cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      checked={preferMideoWitness}
                      onChange={(e) => handleMideoWitnessChange(e.target.checked)}
                      disabled={isApplyingWitnesses}
                      className="h-5 w-5"
                    />
                    <span className="text-lg">I prefer MIDEO employees witness my application.</span>
                  </label>
                  {isApplyingWitnesses && (
                    <p className="mt-2 text-gray-600">Applying witness signatures...</p>
                  )}
                </div>
              )}
              {/* SHOW EMPLOYEE WITNESS CONFIRMATION */}
              {preferMideoWitness && chart?.WITNESS_SIGNATURE?.preferMideoEmployeeWitness && (
                <div className="mt-8 rounded-lg border border-green-200 bg-green-50 p-4">
                  <p className="text-lg font-semibold text-green-800">MIDEO Employee Witnesses Applied</p>
                  <p className="mt-2">Witness 1: {chart.WITNESS_SIGNATURE.witnessName}</p>
                  <p>Witness 2: {chart.WITNESS_SIGNATURE.witness2Name}</p>
                </div>
              )}
              {/* SHOW WITNESS CAPTURE AREAS ONLY IF NOT USING EMPLOYEE WITNESSES */}
              {!preferMideoWitness && (
                <>
              <div id="witness-1-sig-container" className="mt-12">
                <p className="text-2xl font-bold">
                  NOTE: Your Healthcare Agent cannot be either of the two required witnesses below.
                </p>
                <h2 className="mt-12 mr-2 justify-center text-2xl font-bold">{`Primary Witness`}</h2>
                <witnessForm.Field
                  name="witnessName"
                  children={(field) => (
                    <>
                      <FormInput
                        label="Witness 1 Name"
                        id="witness-name"
                        name={field.name}
                        value={field.state.value}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => {
                          setUpdated(false);
                          field.handleChange(e.target.value);
                          setWitnessOneName(e.target.value);
                        }}
                        required={false}
                      />
                    </>
                  )}
                />
                {chart.WITNESS_SIGNATURE && (
                  <WitnessSignatureArea
                    witnessName={witnessOneName}
                    witnessOneEmail={witnessOneEmail}
                    setWitnessOneEmail={setWitnessOneEmail}
                    setWitnessOneIsDocuSign={setWitnessOneIsDocuSign}
                    patientId={patientId}
                    witnessNumber={1}
                    data={chart}
                    patientInfo={patientInfo}
                    isSigning={isSigning}
                    setIsSigning={setIsSigning}
                    elementId={'witness-1-sig-'}
                  />
                )}
              </div>
              <div id="witness-2-sig-container" className="mt-12">
                <h2 className="mr-2 justify-center text-2xl font-bold">{`Second Witness`}</h2>
                <witnessForm.Field
                  name="witness2Name"
                  children={(field) => (
                    <>
                      <FormInput
                        label="Witness 2 Name"
                        id="witness-2-name"
                        name={field.name}
                        value={field.state.value}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => {
                          setUpdated(false);
                          field.handleChange(e.target.value);
                          setWitnessTwoName(e.target.value);
                        }}
                        required={false}
                      />
                    </>
                  )}
                />
                {chart.WITNESS_SIGNATURE && (
                  <WitnessSignatureArea
                    patientId={patientId}
                    witnessName={witnessTwoName}
                    witnessTwoEmail={witnessTwoEmail}
                    setWitnessTwoEmail={setWitnessTwoEmail}
                    setWitnessTwoIsDocuSign={setWitnessTwoIsDocuSign}
                    witnessNumber={2}
                    data={chart}
                    patientInfo={patientInfo}
                    isSigning={isSigning}
                    setIsSigning={setIsSigning}
                    elementId={'witness-2-sig-'}
                  />
                )}
              </div>
                </>
              )}
            </div>
          </div>
          {updated &&
          chart?.PATIENT_SIGNATURE?.signature1DataURI?.length > 0 &&
          (chart?.WITNESS_SIGNATURE?.witnessEmail?.length > 0 ||
            chart?.WITNESS_SIGNATURE?.signature1DataURI?.length > 0) ? (
            <div className="flex flex-col items-center">
              <button
                id="submit-form-info"
                title="Submit Form Info"
                onClick={handleNextButtonClick}
                disabled={false}
                className={classNames.nextButton}
              >
                Next
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center">
              {okToSubmit && (
                <Button
                  id="submit-witness-info"
                  type="submit"
                  textContent="Continue"
                  onClick={handleIdInfoUpdate}
                  disabled={false}
                />
              )}
              {updated && (
                <p className="text-lg text-red-500">
                  WARNING: You cannot complete your application without a witness signature.
                </p>
              )}
            </div>
          )}
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
